import {
    AddOn,
    GetRPOrganizationsResponse,
    RPOrganizationOverview,
    GetSubscriptionsDTO,
    InsightQueryHistory,
    InsightQueryType,
    Organization,
    OrganizationChangelogDTO,
    OrganizationInviteDTO,
    OrganizationOverview,
    OrganizationWithMembers,
    GroupedPickerOptions,
    RPUser,
    UpdateOrganizationDTO,
    colors,
    MediaAsset,
    InterestedOrganizationAdmin,
    InterestedOrganizationDTO,
    InterestedOrganizationsResponse,
    InterestPatchRequest,
    InterestStripeSetupResponse,
    InterestedOrganizationCommunitySize,
    InterestedOrganizationSubscriptionType,
    InterestPricingAmount,
    computePricingQuote,
    computeInterestDemoPricingSummary,
    isDemoInterestRequest,
    shouldAppendCommunitySizeToQuote,
    INTEREST_COMMUNITY_SIZE_OPTIONS,
} from '@hazadapt-git/public-core-base'
import { AxiosError, AxiosResponse } from 'axios'
import { isa } from '../api'
import { toast } from './misc'
import { NewRPUser, RPPaymentMethod } from '../entities'
import { store } from '../store'
import { setOrganization } from '../slices/base'
import { setAddons, setSubscriptions } from '../slices/payment'
import { getEnvVars } from '../config'
import Fuse from 'fuse.js'
import {
    IoAlertCircle,
    IoCheckmark,
    IoCloudUploadOutline,
    IoRefresh,
    IoTrashOutline,
    IoWallet,
} from 'react-icons/io5'
import {
    errorColor,
    infoToastColor,
    primaryIconSize,
    successColor,
} from '../styles/universal'
import { isUserAdmin, isUserOwner } from './permissions'
import { isUserInvited } from './user'

const { apiUrl } = getEnvVars()

export const getSplashOrgDetails = async (): Promise<OrganizationOverview> => {
    const response: AxiosResponse<Organization> = await isa.get('/splash')
    return response.data
}

export const getOrganization = async (): Promise<Organization> => {
    const response: AxiosResponse<Organization> = await isa.get(
        '/organization?overview=true'
    )
    return response.data
}

export const getFullOrganization =
    async (): Promise<OrganizationWithMembers> => {
        const response: AxiosResponse<OrganizationWithMembers> = await isa.get(
            '/organization'
        )
        return response.data
    }

export const getOrganizations =
    async (): Promise<GetRPOrganizationsResponse> => {
        const response: AxiosResponse<GetRPOrganizationsResponse> =
            await isa.get('/organizations')
        return response.data
    }

/** Org slug from `${slug}.${rpUriBase}` tenant hosts (first label before the RP base domain). */
export function rpTenantSlugFromHostname(
    hostname: string,
    rpUriBase: string,
): string | null {
    const suffix = `.${rpUriBase}`
    if (!hostname.endsWith(suffix)) {
        return null
    }
    const withoutBase = hostname.slice(0, -suffix.length)
    if (!withoutBase) {
        return null
    }
    return withoutBase.split('.')[0] ?? null
}

export function membershipOrganizationsFromResponse(
    response: GetRPOrganizationsResponse,
): RPOrganizationOverview[] {
    const yours = response.yours ?? []
    return response.organizations.filter((org) => yours.includes(org.id))
}

export function buildRpTenantHomeUrl(slug: string, rpUriBase: string): string {
    return `https://${slug}.${rpUriBase}/home`
}

export function buildRpApexUrl(rpUriBase: string): string {
    return `https://${rpUriBase}/`
}

/**
 * When Redux has no organization after the initial `/organization` fetch, compares `/organizations`
 * membership with this hostname's org slug: hydrate org, redirect to the user's only org, or apex for picker.
 */
export async function reconcileCurrentOrganizationFromHost(
    loadedOrganization: Organization | null | undefined,
    user: RPUser,
    hostname: string,
    dispatchSetOrganization: (org: Organization) => void,
): Promise<void> {
    if (loadedOrganization || isSupportUser(user) || isUserInvited(user)) {
        return
    }

    const { rpUriBase } = getEnvVars()
    const tenantSlug = rpTenantSlugFromHostname(hostname, rpUriBase)
    if (!rpUriBase || !tenantSlug) {
        return
    }

    const res = await getOrganizations()
    const userOrgs = membershipOrganizationsFromResponse(res)
    const belongsHere = userOrgs.some((org) => org.slug === tenantSlug)

    if (belongsHere) {
        try {
            const org = await getOrganization()
            dispatchSetOrganization(org)
        } catch (err) {
            console.error(err)
        }
        return
    }

    if (userOrgs.length === 1) {
        window.location.href = buildRpTenantHomeUrl(userOrgs[0].slug, rpUriBase)
        return
    }

    window.location.href = buildRpApexUrl(rpUriBase)
}

export const createInterestRequest = async (
    payload: InterestedOrganizationDTO
): Promise<void> => {
    await isa.post('/interest', payload)
}

export const createInterestSetupIntent = async (
    contact_email: string,
    organization_name: string
): Promise<InterestStripeSetupResponse> => {
    const response = await isa.post<InterestStripeSetupResponse>(
        '/interest/stripe-setup',
        { contact_email, organization_name }
    )
    return response.data
}

export const updateInterestSubmission = async (
    id: string,
    payload: InterestPatchRequest
): Promise<void> => {
    await isa.patch(`/interest/submissions/${id}`, payload)
}

export const getInterestedOrganizations = async (): Promise<
    InterestedOrganizationAdmin[]
> => {
    const response: AxiosResponse<InterestedOrganizationsResponse> =
        await isa.get('/interest/submissions')

    if (!response.data.success) {
        throw new Error(
            response.data.error ??
                'Unable to fetch interested organization submissions.'
        )
    }

    return response.data.data ?? []
}

/** Default tier for estimated pricing before community size is selected on the interest form. */
export const INTEREST_FORM_DEFAULT_COMMUNITY_SIZE =
    InterestedOrganizationCommunitySize.UNDER_50K

export function formatInterestPricingAmount(
    amount: InterestPricingAmount
): string {
    return amount.prefix ? `${amount.prefix} ${amount.label}` : amount.label
}

export function getInterestCommunitySizeLabel(
    community_size: InterestedOrganizationCommunitySize | null | undefined
): string | null {
    if (!community_size) return null
    return (
        INTEREST_COMMUNITY_SIZE_OPTIONS.find(
            (option) => option.value === community_size
        )?.label ?? community_size
    )
}

export function buildInterestSubmissionPricingQuote(
    values: Pick<
        InterestedOrganizationDTO,
        'community_size' | 'subscription_type'
    >
): string | undefined {
    if (isDemoInterestRequest(values.subscription_type)) {
        return computeInterestDemoPricingSummary(values.community_size)?.summary
    }

    const quote = computePricingQuote(values.community_size)
    if (!quote) return undefined

    const amount =
        values.subscription_type ===
        InterestedOrganizationSubscriptionType.YEARLY
            ? quote.yearly
            : quote.monthly

    let pricingQuote = formatInterestPricingAmount(amount)

    if (shouldAppendCommunitySizeToQuote(pricingQuote, values.community_size)) {
        const communityLabel = getInterestCommunitySizeLabel(
            values.community_size
        )
        if (communityLabel) {
            pricingQuote = `${pricingQuote} · ${communityLabel}`
        }
    }

    return pricingQuote
}

export const updateOrganization = async (
    org: Partial<OrganizationWithMembers>
): Promise<OrganizationWithMembers> => {
    const { members, ...organization } = org
    const response: AxiosResponse<OrganizationWithMembers> = await isa.patch<
        any,
        AxiosResponse<OrganizationWithMembers>,
        UpdateOrganizationDTO
    >('/organization', { organization, members })
    store.dispatch(setOrganization({ ...response.data }))
    return response.data
}

/**
 * Uploads the user's profile picture to S3
 * @param image the profile picture file
 * @returns a signed URL for the profile picture
 */
export const updateOrganizationLogo = async (
    image: File
): Promise<OrganizationWithMembers> => {
    try {
        toast(
            'Saving...',
            <IoCloudUploadOutline
                color={colors.primary.BLUEBERRY}
                size={primaryIconSize}
            />
        )
        const formData = new FormData()
        formData.append('logo', image)
        const response: AxiosResponse<OrganizationWithMembers> = await isa.put(
            '/organization',
            formData,
            {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true,
            }
        )
        store.dispatch(setOrganization({ ...response.data }))
        toast(
            'Logo updated!',
            <IoCheckmark size={primaryIconSize} color={successColor} />
        )
        return response.data
    } catch (err) {
        toast(
            'There was an error uploading your organization logo. Please try again.',
            <IoAlertCircle color={errorColor} size={primaryIconSize} />,
            'error'
        )
        throw err
    }
}

export const transferOrgOwnership = async (
    user_id: number
): Promise<RPUser[]> => {
    const response: AxiosResponse<RPUser[]> = await isa.put(
        `/organization/transfer-ownership`,
        { member_id: user_id }
    )
    return response.data
}

export const inviteUser = async (newUser: NewRPUser[]): Promise<RPUser[]> => {
    const response: AxiosResponse<RPUser[]> = await isa.post('/member', newUser)
    return response.data
}

export const updateUser = async (user: RPUser): Promise<RPUser[]> => {
    const response: AxiosResponse<RPUser[]> = await isa.put(
        `/member/${user.rp_user_id}`,
        user
    )
    return response.data
}

export const removeUser = async (
    userIds: number[]
): Promise<OrganizationWithMembers> => {
    const response: AxiosResponse<OrganizationWithMembers> = await isa.delete(
        `/member?user_id=${userIds.join(',')}`
    )
    await getSubscriptions()
    return response.data
}

export const getInvite = async (
    inviteCode: string
): Promise<OrganizationInviteDTO> => {
    const response: AxiosResponse<OrganizationInviteDTO> = await isa.get(
        `/invite/${inviteCode}`
    )
    return response.data
}

export const getSavedQueries = async (
    page = 1,
    queryType?: InsightQueryType
): Promise<InsightQueryHistory> => {
    const url = new URL(`${isa.defaults.baseURL ?? apiUrl}/queries`)
    url.searchParams.append('page', page.toString())
    if (queryType) url.searchParams.append('query_type', queryType)
    const response: AxiosResponse<InsightQueryHistory> = await isa.get(
        url.toString()
    )
    return response.data
}

export const getOrganizationChangelog = async (
    page = 1
): Promise<OrganizationChangelogDTO> => {
    const response: AxiosResponse<OrganizationChangelogDTO> = await isa.get(
        `/changelog/organization?page=${page}`
    )
    return response.data
}

export const getMediaAssets = async (): Promise<MediaAsset[]> => {
    const response: AxiosResponse<MediaAsset[]> = await isa.get(
        `/organization/assets`
    )
    return response.data
}

export const getAvailableAddons = async (): Promise<void> => {
    try {
        const response: AxiosResponse<AddOn[]> = await isa.get('/add-ons')
        store.dispatch(setAddons(response.data))
    } catch (err) {
        console.error(err)
    }
}

export const getSubscriptions = async (): Promise<void> => {
    try {
        const response: AxiosResponse<GetSubscriptionsDTO> = await isa.get(
            '/subscription'
        )
        store.dispatch(setSubscriptions(response.data))
    } catch (err) {
        console.error(err)
    }
}

export const cancelSubscription = async (
    subscription_id: string
): Promise<void> => {
    try {
        const response: AxiosResponse<GetSubscriptionsDTO> = await isa.post(
            `/subscription/${subscription_id}/cancel`
        )
        store.dispatch(setSubscriptions(response.data))
    } catch (err) {
        const error = err as AxiosError
        throw error
    }
}

export const restoreSubscription = async (
    subscription_id: string
): Promise<void> => {
    try {
        toast(
            'Restoring subscription...',
            <IoRefresh color={infoToastColor} size={primaryIconSize} />
        )
        const response: AxiosResponse<GetSubscriptionsDTO> = await isa.post(
            `/subscription/${subscription_id}/restore`
        )
        store.dispatch(setSubscriptions(response.data))
        toast(
            'Restored subscription!',
            <IoCheckmark color={successColor} size={primaryIconSize} />
        )
    } catch (err) {
        const error = err as AxiosError
        console.error(error)
        toast(
            `Unable to restore subscription. Please try again. If the problem continues, please contact HazAdapt.\nReason: ${error.message}.`,
            <IoAlertCircle color={errorColor} size={primaryIconSize} />,
            'error'
        )
    }
}

export const updateDefaultPaymentMethod = async (
    payment_method: RPPaymentMethod
): Promise<void> => {
    try {
        toast(
            'Updating default payment method...',
            <IoWallet color={infoToastColor} size={primaryIconSize} />
        )
        const response: AxiosResponse<GetSubscriptionsDTO> = await isa.put(
            '/payment-method/default',
            {
                payment_method_id: payment_method.id,
            }
        )
        store.dispatch(setSubscriptions(response.data))

        const ToastIcon = (
            <IoCheckmark color={successColor} size={primaryIconSize} />
        )
        if (isBankAccount(payment_method.type)) {
            toast(
                `Bank account${
                    payment_method.last4
                        ? ` ending in ${payment_method.last4}`
                        : ''
                } is now your organization's default payment method.`,
                ToastIcon
            )
        } else if (isCardPayment(payment_method.type)) {
            toast(
                `Card${
                    payment_method.last4
                        ? ` ending in ${payment_method.last4}`
                        : ''
                } is now your organization's default payment method.`,
                ToastIcon
            )
        } else {
            toast(
                `Successfully updated your organization's default payment method`,
                ToastIcon
            )
        }
    } catch (err) {
        console.error('Failed to delete payment method:', err)
        toast(
            "Unable to update your organization's default payment method. Please try again. If the problem persists, please contact HazAdapt.",
            <IoAlertCircle color={errorColor} size={primaryIconSize} />,
            'error'
        )
    }
}

export const updateSubscriptionPaymentMethod = async (
    subscription_id: string,
    payment_method: RPPaymentMethod
): Promise<void> => {
    try {
        toast(
            'Updating payment method...',
            <IoWallet color={infoToastColor} size={primaryIconSize} />
        )
        await isa.put(`/subscription/${subscription_id}/payment-method`, {
            payment_method_id: payment_method.id,
        })
        toast(
            'Successfully updated payment method!',
            <IoCheckmark color={successColor} size={primaryIconSize} />
        )
    } catch (err) {
        console.error(err)
        toast(
            'Unable to update payment method. Please try again. If the problem persists, please contact HazAdapt.',
            <IoAlertCircle color={errorColor} size={primaryIconSize} />,
            'error'
        )
    } finally {
        await getSubscriptions()
    }
}

export const deletePaymentMethod = async (
    payment_method: RPPaymentMethod
): Promise<void> => {
    try {
        toast(
            'Deleting payment method...',
            <IoWallet color={infoToastColor} size={primaryIconSize} />
        )
        const response: AxiosResponse<GetSubscriptionsDTO> = await isa.delete(
            `/payment-method/${payment_method.id}`
        )
        store.dispatch(setSubscriptions(response.data))

        const ToastIcon = (
            <IoTrashOutline color={successColor} size={primaryIconSize} />
        )
        if (isBankAccount(payment_method.type)) {
            toast(
                `Deleted bank account${
                    payment_method.last4
                        ? ` ending in ${payment_method.last4}`
                        : ''
                } from your organization's list of payment methods. Please review any subscriptions this bank account is linked to.`,
                ToastIcon
            )
        } else if (isCardPayment(payment_method.type)) {
            toast(
                `Removed card${
                    payment_method.last4
                        ? ` ending in ${payment_method.last4}`
                        : ''
                } from your organization's list of payment methods. Please review any subscriptions this card is linked to.`,
                ToastIcon
            )
        } else {
            toast(
                'Removed payment method. Please review any subscriptions this payment method is linked to.',
                ToastIcon
            )
        }
    } catch (err) {
        console.error('Failed to delete payment method:', err)
        toast(
            "Unable to remove payment method from your organization's list of payment methods. Please try again. If the problem persists, please contact HazAdapt.",
            <IoAlertCircle color={errorColor} size={primaryIconSize} />,
            'error'
        )
    }
}

export const buildAreaPickerOptions = (
    organization: Organization,
    selectedAreas: string[] = []
): GroupedPickerOptions<string>[] => {
    const hasStates = selectedAreas.some((a) =>
        organization.areas.state.includes(a)
    )
    const hasCounties = selectedAreas.some((a) =>
        organization.areas.county.includes(a)
    )
    const hasZips = selectedAreas.some((a) =>
        organization.areas.zip.includes(a)
    )
    const zips = [...organization.areas.zip]
    const counties = [...organization.areas.county]
    const states = [...organization.areas.state]

    const sortSelectedToFront = (a: string, b: string) => {
        if (selectedAreas.includes(a) && !selectedAreas.includes(b)) return -1
        if (selectedAreas.includes(b) && !selectedAreas.includes(a)) return 1
        return a < b ? -1 : 1
    }
    zips.sort(sortSelectedToFront)
    counties.sort((a, b) => {
        if (selectedAreas.includes(a) && !selectedAreas.includes(b)) return -1
        if (selectedAreas.includes(b) && !selectedAreas.includes(a)) return 1
        const aPieces = a.split(', ')
        const bPieces = b.split(', ')
        const aState = aPieces[aPieces.length - 1]
        const bState = bPieces[bPieces.length - 1]
        if (aState !== bState) return aState < bState ? -1 : 1 // Sort by state
        return a < b ? -1 : 1 // Same state; sort by county
    })
    states.sort(sortSelectedToFront)
    const zipOptions: GroupedPickerOptions<string> = {
        id: 'zips',
        label: 'ZIP Codes',
        options: zips.map((z) => ({
            label: `Zip code ${z}`,
            value: z,
            disabled: hasCounties || hasStates,
        })),
    }
    const countyOptions: GroupedPickerOptions<string> = {
        id: 'counties',
        label: 'Counties',
        options: counties.map((c) => ({
            label: c,
            value: c,
            disabled: hasZips || hasStates,
        })),
    }
    const stateOptions: GroupedPickerOptions<string> = {
        id: 'states',
        label: 'States',
        options: states.map((s) => ({
            label: s,
            value: s,
            disabled: hasZips || hasCounties,
        })),
    }
    const groups: GroupedPickerOptions<string>[] = [
        {
            label: '',
            id: 'all',
            options: [
                {
                    label:
                        organization.jurisdiction_name ?? 'Full Jurisdiction',
                    value: '',
                },
            ],
        },
    ]
    if (hasZips) {
        groups.push(zipOptions, stateOptions, countyOptions)
    } else if (hasCounties) {
        groups.push(countyOptions, stateOptions, zipOptions)
    } else {
        groups.push(stateOptions, countyOptions, zipOptions)
    }

    // Only include groups with at least one option
    const options = groups.filter((g) => g.options.length > 0)
    return options
}

export const getAvailableOrganizationAreas = (
    groups: GroupedPickerOptions<string>[]
): [string[], string[], string[]] => {
    const availableZips = (
        groups.find((g) => g.id === 'zips')?.options ?? []
    ).map((o) => o.value)
    const availableCounties = (
        groups.find((g) => g.id === 'counties')?.options ?? []
    ).map((o) => o.value)
    const availableStates = (
        groups.find((g) => g.id === 'states')?.options ?? []
    ).map((o) => o.value)
    return [availableZips, availableCounties, availableStates]
}

export const sortOrgMembers = (a: RPUser, b: RPUser): number => {
    const aAdmin = isUserAdmin(a)
    const bAdmin = isUserAdmin(b)
    const aOwner = isUserOwner(a)
    const bOwner = isUserOwner(b)
    if (a.status === 'invited' && b.status !== 'invited') return 1
    if (a.status !== 'invited' && b.status === 'invited') return -1
    if (a.status === 'invited' && b.status === 'invited') {
        if (a.date_created && b.date_created) {
            return a.date_created < b.date_created ? -1 : 1
        } else {
            return a.last_name < b.last_name ? -1 : 1
        }
    }
    if (aOwner && !bOwner) return -1
    if (bOwner && !aOwner) return 1
    if (aAdmin && !bAdmin) return -1
    if (bAdmin && !aAdmin) return 1
    if (a.date_created && b.date_created) {
        return a.date_created < b.date_created ? -1 : 1
    } else {
        return a.last_name < b.last_name ? -1 : 1
    }
}

export function searchTeam(team: RPUser[], query: string): { team: RPUser[] } {
    const teamWithFullName = team.map((member) => ({
        ...member,
        full_name: `${member.first_name} ${member.last_name}`,
    }))
    const options = {
        keys: [
            { name: 'full_name', weight: 4 },
            { name: 'email', weight: 3 },
            { name: 'title', weight: 2 },
        ],
        threshold: 0.3,
        distance: 100,
    }
    if (query.length === 0) return { team }
    const fuse = new Fuse(teamWithFullName, options)

    const filteredTeam = fuse.search(query).map((result) => result.item)

    return { team: filteredTeam }
}

export const isCardPayment = (type: string) =>
    ['Credit Card', 'Debit Card', 'Apple Pay', 'Google Pay'].includes(type)

export const isBankAccount = (type: string) => type === 'Bank Account'

export const isSupportUser = (user?: RPUser | null): boolean => {
    return user?.email?.toLowerCase() === 'support@hazadapt.com'
}

export type {
    InterestedOrganization,
    InterestedOrganizationAdmin,
    InterestedOrganizationDTO,
    InterestedOrganizationCommunitySize,
    InterestedOrganizationReferralSource,
    InterestedOrganizationSubscriptionType,
    InterestedOrganizationsResponse,
    InterestPatchRequest,
    InterestStripeSetupResponse,
    PricingQuoteResult,
    InterestDemoPricingSummary,
    InterestPricingAmount,
} from '@hazadapt-git/public-core-base'

export {
    computePricingQuote,
    computeInterestDemoPricingSummary,
    isDemoInterestRequest,
    formatInterestSubscriptionLabel,
    shouldAppendCommunitySizeToQuote,
    INTEREST_PRICING,
    INTEREST_COMMUNITY_SIZE_OPTIONS,
    INTEREST_SUBSCRIPTION_OPTIONS,
    INTEREST_REFERRAL_SOURCE_OPTIONS,
} from '@hazadapt-git/public-core-base'
